import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import OperatorLayout from "./OperatorLayout";
import { Button } from "@/components/ui/button";
import { useOperatorApi } from "@/hooks/useOperatorApi";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Plus, Building2 } from "lucide-react";
import RegistrationFormDialog, { type RegistrationFormData } from "@/components/operator/RegistrationFormDialog";
import RegistrationDetail from "@/components/operator/RegistrationDetail";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Registration {
  id: string;
  status: string;
  company_name: string;
  contact_name: string;
  contact_email: string;
  phone: string | null;
  city: string | null;
  region: string | null;
  categories: string[];
  created_at: string;
}

const FILTERS = ["pending", "approved", "rejected", "all"] as const;

export default function OperatorRegistrations() {
  const { t } = useTranslation();
  const { operatorFetch } = useOperatorApi();
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("pending");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  // Dialog states
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const FILTER_LABELS: Record<string, string> = {
    pending: t("op.regFilterPending"),
    approved: t("op.regFilterApproved"),
    rejected: t("op.regFilterRejected"),
    all: t("op.regFilterAll"),
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const data = await operatorFetch(`registrations${params}`);
      setRegistrations(data || []);
    } catch {
      setRegistrations([]);
    } finally {
      setLoading(false);
    }
  }, [operatorFetch, filter]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const fetchDetail = async (id: string) => {
    setSelectedId(id);
    setDetailLoading(true);
    try {
      const data = await operatorFetch(`registrations/${id}`);
      setDetail(data);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedId) return;
    setApproving(true);
    try {
      await operatorFetch(`registrations/${selectedId}/approve`, { method: "POST" });
      toast.success(t("op.regApproveSuccess"));
      setSelectedId(null);
      setDetail(null);
      fetchList();
    } catch (err: any) {
      toast.error(err.message || t("op.regApproveError"));
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!selectedId) return;
    setRejecting(true);
    try {
      await operatorFetch(`registrations/${selectedId}/reject`, { method: "POST" });
      toast.success(t("op.regRejectSuccess"));
      setSelectedId(null);
      setDetail(null);
      fetchList();
    } catch (err: any) {
      toast.error(err.message || t("op.regRejectError"));
    } finally {
      setRejecting(false);
    }
  };

  const handleCreate = async (data: RegistrationFormData) => {
    await operatorFetch("registrations", { method: "POST", body: data });
    toast.success(t("op.regCreateSuccess"));
    setFormOpen(false);
    fetchList();
  };

  const handleUpdate = async (data: RegistrationFormData) => {
    if (!selectedId) return;
    const updated = await operatorFetch(`registrations/${selectedId}`, { method: "PATCH", body: data });
    toast.success(t("op.regUpdateSuccess"));
    setFormOpen(false);
    setDetail(updated);
    fetchList();
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    setDeleting(true);
    try {
      await operatorFetch(`registrations/${selectedId}`, { method: "DELETE" });
      toast.success(t("op.regDeleteSuccess"));
      setDeleteOpen(false);
      setSelectedId(null);
      setDetail(null);
      fetchList();
    } catch (err: any) {
      toast.error(err.message || t("op.regDeleteError"));
    } finally {
      setDeleting(false);
    }
  };

  const openCreate = () => {
    setFormMode("create");
    setFormOpen(true);
  };

  const openEdit = () => {
    if (!detail) return;
    setFormMode("edit");
    setFormOpen(true);
  };

  const statusBadge = (status: string) => {
    const map: Record<string, { label: string; cls: string }> = {
      pending: { label: t("op.regFilterPending"), cls: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
      approved: { label: t("op.regFilterApproved"), cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
      rejected: { label: t("op.regFilterRejected"), cls: "bg-destructive/10 text-destructive border-destructive/20" },
    };
    const s = map[status] || map.pending;
    return <span className={cn("text-xs px-2 py-0.5 border", s.cls)}>{s.label}</span>;
  };

  return (
    <OperatorLayout title={t("op.navRegistrations")} subtitle={t("op.regSubtitle")}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* List */}
        <div className="lg:col-span-5 space-y-4">
          {/* Filter + Create */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  onClick={() => { setFilter(f); setSelectedId(null); setDetail(null); }}
                  className={cn(
                    "px-3 py-1.5 text-xs border transition-colors",
                    filter === f ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:text-foreground"
                  )}
                >
                  {FILTER_LABELS[f]}
                </button>
              ))}
            </div>
            <Button variant="commit" size="sm" onClick={openCreate} className="gap-1.5">
              <Plus className="w-4 h-4" />
              {t("op.regNew")}
            </Button>
          </div>

          {loading ? (
            <p className="text-caption p-8 text-center">{t("common.loading")}</p>
          ) : registrations.length === 0 ? (
            <div className="p-12 border border-dashed border-border text-center">
              <Building2 className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
              <p className="text-caption">{t("op.regEmpty")}</p>
            </div>
          ) : (
            <div className="divide-y divide-border border border-border">
              {registrations.map((r) => (
                <button
                  key={r.id}
                  onClick={() => fetchDetail(r.id)}
                  className={cn(
                    "w-full text-left p-4 hover:bg-secondary/20 transition-colors",
                    selectedId === r.id && "bg-secondary/30"
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.company_name}</p>
                      <p className="text-caption truncate">{r.contact_name} · {r.contact_email}</p>
                      <p className="text-micro mt-1">
                        {new Date(r.created_at).toLocaleDateString("de-DE")}
                        {r.city && ` · ${r.city}`}
                      </p>
                    </div>
                    {statusBadge(r.status)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-7">
          {!selectedId ? (
            <div className="h-full min-h-[400px] flex items-center justify-center border border-dashed border-border p-12">
              <p className="text-caption text-center">{t("op.regSelectHint")}</p>
            </div>
          ) : detailLoading ? (
            <div className="p-12 text-center text-caption">{t("common.loading")}</div>
          ) : detail ? (
            <RegistrationDetail
              detail={detail}
              onApprove={handleApprove}
              onReject={handleReject}
              onEdit={openEdit}
              onDelete={() => setDeleteOpen(true)}
              approving={approving}
              rejecting={rejecting}
            />
          ) : null}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <RegistrationFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={formMode === "create" ? handleCreate : handleUpdate}
        initialData={formMode === "edit" ? detail : null}
        mode={formMode}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("op.regDeleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("op.regDeleteDesc", { name: detail?.company_name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? t("common.loading") : t("op.regDeleteConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </OperatorLayout>
  );
}